import { Box, Button, Divider, Typography, useMediaQuery } from '@mui/material';
import { forwardRef, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import * as React from 'react';

import { useCurrency } from '../../../contexts/CurrencyProvider';
import { IOneLinkChunkFormProps, TOLInventoryInfo } from '../../../contexts/OneLinkSummaryProvider';
import { ICampaign, ICampaignItem, ICampaignSummary } from '../../../types/campaigns';
import { IInventoryItem, InventoryTypesEnum, IOnDemandItem } from '../../../types/inventories';
import { BoxDetailsSidebarModeEnum } from '../../../types/shell';
import { getCanRemoveItemFromCampaign, getIsItemLastVisibleInCampaign } from '../../../utils/campaigns';
import { isObjectsEqual } from '../../../utils/helpers';
import { mapItemFromState, mapMskuItemToRequest } from '../../../utils/inventories';
import { BoxDetailsItem, Price } from '../../index';
import { FormControls } from '../../SummaryComponentMUI/components/common';
import { AddCampaign, AddCampaigns, AddInventoryItems, CampaignListItem } from '../../_mui/_components_';
import { actions, reducer } from './reducer';

import variables from '../../../contexts/ThemeProvider/variables';

enum SidebarMode {
  AddCampaign = 'add-campaign',
  AddCampaigns = 'add-campaigns',
  AddItems = 'add-items',
}

const InventoryForm = forwardRef(({ onSubmit, instance, onClose }: IOneLinkChunkFormProps, ref: any) => {
  const { getCampaignTotal } = useCurrency();
  const [sidebarMode, setSidebarMode] = useState<SidebarMode | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const initialReducerState = {
    pick_campaign_ids: instance?.pick_campaign_ids || null,
    pick_campaigns: instance?.pick_campaigns || undefined,
    items: instance?.items || undefined,
    pre_created_engagement_id: instance?.pre_created_engagement_id || '',
    campaign_name: instance?.campaign_name || '',
  };

  const [state, dispatch] = useReducer(reducer, initialReducerState);
  const { items, pre_created_engagement_id, campaign_name, pick_campaigns, pick_campaign_ids } = state;

  const isPYG = useMemo(() => pick_campaign_ids && pick_campaign_ids.length > 0, [pick_campaign_ids]);
  const isSingleCampaign = useMemo(() => pick_campaigns && pick_campaigns.length === 1, [pick_campaigns]);
  const isCustomSend = useMemo(() => !isPYG && !isSingleCampaign, [isPYG, isSingleCampaign]);
  const canRemoveAll = useMemo(() => false, []);

  const boxDisplayedPrice = React.useMemo(() => {
    if (items) {
      return getCampaignTotal({ items });
    }
  }, [getCampaignTotal, items]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e?.preventDefault?.();

      const inventory = isPYG ? { pick_campaign_ids } : { campaign_name, items, pre_created_engagement_id };

      const preparedItems = inventory.items?.map((item) =>
        item.type === InventoryTypesEnum.MSKU ? mapMskuItemToRequest(item) : item,
      );

      onSubmit({
        inventory_info: {
          ...inventory,
          items: preparedItems,
        } as TOLInventoryInfo,
      });

      setIsDirty(false);
      ref.current = false;
    },
    [isPYG, pre_created_engagement_id, campaign_name, items, pick_campaign_ids, onSubmit],
  );

  const header = useMemo(() => {
    let title;
    let text;

    switch (true) {
      case !!campaign_name:
        title = campaign_name;
        text = 'These items are included in this campaign.';
        break;
      case isPYG:
        title = 'Campaign Options';
        text = 'The recipient can select from these campaigns.';
        break;
      default:
        title = 'Custom send';
        text = 'These items are included in send.';
        break;
    }

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        {text && <Typography variant="body2">{text}</Typography>}
      </Box>
    );
  }, [isPYG, campaign_name]);

  const currentItems = useMemo(
    () => state.items && state.items.length > 0 && mapItemFromState(state.items as ICampaignItem[]),
    [state.items],
  );

  const initialItems = useMemo(() => {
    return (
      initialReducerState.items && initialReducerState.items.length > 0 && mapItemFromState(initialReducerState.items)
    );
  }, [initialReducerState?.items]);

  const hasSameItems = useMemo(() => isObjectsEqual(currentItems, initialItems), [currentItems, initialItems]);

  const hasSameCampaigns = useMemo(() => {
    return (
      initialReducerState?.pick_campaign_ids &&
      state.pick_campaign_ids &&
      isObjectsEqual(initialReducerState?.pick_campaign_ids, state.pick_campaign_ids)
    );
  }, [initialReducerState?.pick_campaign_ids, state.pick_campaign_ids]);

  const isItems = useMemo(() => state.items && state.items?.length > 0, [state.items]);
  const isCampaigns = useMemo(
    () => state.pick_campaign_ids && state.pick_campaign_ids.length > 0,
    [state.pick_campaign_ids],
  );

  const handleRemoveItem = useCallback((id: string) => {
    dispatch(actions.removeItem(id));
  }, []);

  const handleAddItems = useCallback((itemsList: IInventoryItem[]) => {
    dispatch(actions.addItems(itemsList));
  }, []);

  const handleRemoveCampaign = useCallback((id: string) => {
    dispatch(actions.removeCampaign(id));
  }, []);

  const handleAddCampaigns = useCallback((campaigns: ICampaignSummary[]) => {
    dispatch(actions.addCampaigns(campaigns));
  }, []);

  const handleAddSingleCampaign = useCallback((campaign: ICampaignSummary) => {
    dispatch(actions.addCampaign(campaign));
  }, []);

  const itemMarkup = useCallback(
    (item: ICampaignItem | IOnDemandItem, itemsList: ICampaignItem[] | IOnDemandItem[]) => {
      const { item_id } = item;
      const isLastVisibleItem = getIsItemLastVisibleInCampaign(itemsList, item_id);
      const isCustomizableItem = item.item_customization_allowed;
      const canRemoveItem = canRemoveAll
        ? getCanRemoveItemFromCampaign(itemsList, item.item_id)
        : itemsList?.length > 1 && getCanRemoveItemFromCampaign(itemsList, item.item_id);

      return (
        <Box key={item_id} sx={{ marginX: 0.5 }}>
          <BoxDetailsItem
            item={item as ICampaignItem | IOnDemandItem}
            canHide={!isLastVisibleItem && !isCustomizableItem}
            key={item.item_id}
            mode={BoxDetailsSidebarModeEnum.Edit}
            onChange={(payload) => dispatch(actions.replaceQuantity(payload))}
            onRemove={canRemoveItem ? ({ item_id: id }) => handleRemoveItem(id) : undefined}
          />
        </Box>
      );
    },
    [handleRemoveItem, canRemoveAll],
  );

  const campaignItemMarkup = useCallback(
    (campaign: ICampaign) => {
      const total = getCampaignTotal(campaign);
      const { box_id: boxId } = campaign;

      return (
        <CampaignListItem campaign={campaign} key={boxId} total={total} onRemove={() => handleRemoveCampaign(boxId)} />
      );
    },
    [handleRemoveCampaign, getCampaignTotal],
  );

  const renderItem = useCallback(
    (item: ICampaignItem | IOnDemandItem | ICampaign) => {
      if (isCustomSend || isSingleCampaign) {
        return itemMarkup(item as IOnDemandItem | ICampaignItem, (items as ICampaignItem[]) || []);
      }
      return campaignItemMarkup(item as ICampaign);
    },
    [isSingleCampaign, isCustomSend, items, itemMarkup, campaignItemMarkup],
  );

  const handleModeButtonAction = useCallback(() => {
    return !!isPYG ? setSidebarMode(SidebarMode.AddCampaign) : setSidebarMode(SidebarMode.AddCampaigns);
  }, [isPYG]);

  const handleAddButtonAction = useCallback(() => {
    return !!isPYG ? setSidebarMode(SidebarMode.AddCampaigns) : setSidebarMode(SidebarMode.AddItems);
  }, [isPYG]);

  const renderList = useCallback(() => {
    // @ts-ignore type mismatch  - items: IInventoryItem[], pick_campaigns: ICampaignSummary[],
    // TODO: fix this
    // renderItem: (item: ICampaignItem | IOnDemandItem | ICampaign) => JSX.Element
    return (pick_campaign_ids?.length ? pick_campaigns : items)?.map(renderItem);
  }, [pick_campaign_ids, pick_campaigns, items, renderItem]);

  const isHeight1800 = useMediaQuery('(min-height:1800px)');
  const isHeight1400 = useMediaQuery('(min-height:1400px)');
  const isHeight1000 = useMediaQuery('(min-height:1000px)');
  const isHeight900 = useMediaQuery('(min-height:900px)');

  const getMaxHeight = useCallback((): number => {
    switch (true) {
      case isHeight1800 || isHeight1400:
        return 900;
      case isHeight1000:
        return 550;
      case isHeight900:
        return 400;
      default:
        return 330;
    }
  }, [isHeight900, isHeight1000, isHeight1400, isHeight1800]);

  const handleCancel = useCallback(() => {
    onClose();
    setIsDirty(false);
    ref.current = false;
  }, [onClose]);

  useEffect(() => {
    const itemsChanged = !hasSameItems;
    const campaignsChanged = !hasSameCampaigns;

    let dirty: boolean;

    switch (true) {
      case isItems:
        dirty = itemsChanged;
        break;
      case isCampaigns:
        dirty = campaignsChanged;
        break;
      default:
        dirty = itemsChanged || campaignsChanged;
        break;
    }

    ref.current = dirty;
    setIsDirty(dirty);
  }, [
    isItems,
    isCampaigns,
    currentItems,
    initialItems,
    state.pick_campaign_ids,
    initialReducerState.pick_campaign_ids,
  ]);

  return (
    <Box sx={{ height: '100%' }}>
      {sidebarMode === SidebarMode.AddCampaign && (
        <AddCampaign onClose={() => setSidebarMode(null)} onSelect={handleAddSingleCampaign} />
      )}
      {sidebarMode === SidebarMode.AddCampaigns && (
        <AddCampaigns onClose={() => setSidebarMode(null)} value={pick_campaign_ids} onSubmit={handleAddCampaigns} />
      )}
      {sidebarMode === SidebarMode.AddItems && (
        <AddInventoryItems
          onClose={() => setSidebarMode(null)}
          items={items as IInventoryItem[]}
          onSubmit={handleAddItems}
        />
      )}
      <Box
        component="form"
        role="form"
        sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        onSubmit={handleSubmit}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: '1 1 auto', minHeight: 0 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" sx={{ flexGrow: 1 }} onClick={handleModeButtonAction}>
              {!!isPYG ? 'Convert to Single Campaign' : 'Convert to Pick Your Gift'}
            </Button>
            <Button variant="contained" sx={{ color: variables.white }} onClick={handleAddButtonAction}>
              {!!isPYG ? 'Add Campaign' : 'Add Items'}
            </Button>
          </Box>
          <Divider orientation="horizontal" flexItem />
          {header}
          <Box
            sx={{
              flex: '1 1 auto',
              minHeight: 0,
              overflow: 'auto',
              maxHeight: getMaxHeight(),
            }}
          >
            {renderList()}
          </Box>
          {!isPYG && (
            <Box sx={{ m: 2, display: 'flex', flexDirection: 'column', alignItems: 'end' }}>
              <Typography sx={{ color: variables.grayEbony }}>Total cost</Typography>
              <Price value={boxDisplayedPrice!} big />
            </Box>
          )}
        </Box>
        <FormControls onCancel={handleCancel} isDisabled={!isDirty} />
      </Box>
    </Box>
  );
});

export default InventoryForm;
